import {
  AuthenticationDetails,
  CodeDeliveryDetails,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
  IAuthenticationCallback,
  ICognitoUserAttributeData,
  IMfaSettings,
} from 'amazon-cognito-identity-js';

const userPool = new CognitoUserPool({
  UserPoolId: PRISMA_CONFIG.authentication.poolId,
  ClientId: PRISMA_CONFIG.authentication.clientId,
  AdvancedSecurityDataCollectionFlag: true,
});

export type CognitoFailure = {
  code:
    | 'InvalidPasswordException'
    | 'UserNotFoundException' // custom exception we defined
    | 'NotAuthorizedException'
    | 'PasswordResetRequiredException';
  name: CognitoFailure['code']; // just duplicate of code?
  message: string;
};

export type AuthenticationResult =
  | { type: 'success'; session: CognitoUserSession }
  // not every error is a CognitoFailure
  // you can also get things like "is not a function" errors
  | { type: 'error'; error: CognitoFailure | any }
  | { type: 'new-password'; cognitoUser: CognitoUser }
  | { type: 'select-mfa'; cognitoUser: CognitoUser }
  | { type: 'mfa-setup'; cognitoUser: CognitoUser }
  | {
      type: 'mfa-required';
      cognitoUser: CognitoUser;
      challengeParameters: {
        CODE_DELIVERY_DELIVERY_MEDIUM: 'SMS';
        CODE_DELIVERY_DESTINATION: string;
      };
    }
  | { type: 'totp-required'; cognitoUser: CognitoUser };

export type AuthenticationParams = {
  email: string;
  password: string;
};

export function authenticate({ email, password }: AuthenticationParams) {
  const authenticationDetails = new AuthenticationDetails({
    Username: email,
    Password: password,
  });

  const cognitoUser = new CognitoUser({
    Username: email,
    Pool: userPool,
  });

  cognitoUser.setAuthenticationFlowType('USER_PASSWORD_AUTH');

  return new Promise<AuthenticationResult>((resolve, reject) =>
    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess(session) {
        resolve({ type: 'success', session });
      },
      onFailure(error: CognitoFailure | any) {
        resolve({ type: 'error', error });
      },
      newPasswordRequired() {
        resolve({ type: 'new-password', cognitoUser });
      },
      // needed initially
      mfaSetup() {
        resolve({ type: 'mfa-setup', cognitoUser });
      },
      // if mfa-setup was done (you can use totp/'SOFTWARE_TOKEN_MFA') and
      // also phone_number was set (you can use sms/'SMS_MFA')
      selectMFAType(challengeName, challengeParameters) {
        resolve({ type: 'select-mfa', cognitoUser });
      },
      totpRequired() {
        resolve({ type: 'totp-required', cognitoUser });
      },
      // will be called on login to enter a SMS code
      mfaRequired(
        challengeName,
        challengeParameters: {
          CODE_DELIVERY_DELIVERY_MEDIUM: 'SMS';
          CODE_DELIVERY_DESTINATION: string;
        }
      ) {
        resolve({
          type: 'mfa-required',
          cognitoUser,
          challengeParameters,
        });
      },
    })
  );
}

export type SetNewPasswordParams = {
  cognitoUser: CognitoUser;
  newPassword: string;
};

export type NewPasswordResult =
  | { type: 'success'; session: CognitoUserSession }
  | {
      type: 'mfa-required';
      challengeParameters: {
        CODE_DELIVERY_DELIVERY_MEDIUM: 'SMS';
        CODE_DELIVERY_DESTINATION: string;
      };
    }
  | { type: 'mfa-setup' }
  | { type: 'select-mfa' }
  | { type: 'totp-required' };

export async function setNewPassword({
  cognitoUser,
  newPassword,
}: SetNewPasswordParams) {
  return new Promise<NewPasswordResult>((resolve, reject) => {
    // to workaround incorrect Cognito typings, we need to use an explicit type here
    // see https://github.com/aws-amplify/amplify-js/pull/6708
    const callbacks: IAuthenticationCallback = {
      onSuccess(session) {
        resolve({ type: 'success', session });
      },
      onFailure: reject,
      // needed initially
      mfaSetup() {
        resolve({ type: 'mfa-setup' });
      },
      // will be called on login to enter a SMS code
      mfaRequired(
        challengeName,
        challengeParameters: {
          CODE_DELIVERY_DELIVERY_MEDIUM: 'SMS';
          CODE_DELIVERY_DESTINATION: string;
        }
      ) {
        resolve({ type: 'mfa-required', challengeParameters });
      },
      selectMFAType(challengeName, challengeParameters) {
        resolve({ type: 'select-mfa' });
      },
      totpRequired() {
        resolve({ type: 'totp-required' });
      },
    };
    cognitoUser.completeNewPasswordChallenge(newPassword, null, callbacks);
  });
}

export type GetSecretForSoftwareTokenParams = {
  cognitoUser: CognitoUser;
};

export async function getSecretForSoftwareToken({
  cognitoUser,
}: GetSecretForSoftwareTokenParams) {
  return new Promise<string>((resolve, reject) =>
    cognitoUser.associateSoftwareToken({
      associateSecretCode: resolve,
      onFailure: reject,
    })
  );
}

export type VerifySoftwareTokenParams = {
  verificationCode: string;
  cognitoUser: CognitoUser;
};

export async function verifySoftwareToken({
  verificationCode,
  cognitoUser,
}: VerifySoftwareTokenParams) {
  return new Promise<CognitoUserSession>((resolve, reject) =>
    cognitoUser.verifySoftwareToken(verificationCode, 'TOTP Device', {
      onSuccess: resolve,
      onFailure: reject,
    })
  );
}

export type SendMfaParams = {
  cognitoUser: CognitoUser;
  verificationCode: string;
  mfaType?: 'SOFTWARE_TOKEN_MFA' | 'SMS_MFA';
};

export async function sendMfaCode({
  cognitoUser,
  verificationCode,
  mfaType = 'SOFTWARE_TOKEN_MFA',
}: SendMfaParams) {
  return new Promise<CognitoUserSession>((resolve, reject) =>
    // sendMFACode call RespondToAuthChallenge internally, so we need to look up this API to see all possible errors
    // https://docs.aws.amazon.com/de_de/cognito-user-identity-pools/latest/APIReference/API_RespondToAuthChallenge.html
    cognitoUser.sendMFACode(
      verificationCode,
      {
        onSuccess: resolve,
        onFailure: reject,
      },
      mfaType
    )
  );
}

export type ForgotPasswordParams = {
  email: string;
};

export type ForgotPasswordResult = { CodeDeliveryDetails: CodeDeliveryDetails };

export async function forgotPassword({ email }: ForgotPasswordParams) {
  const cognitoUser = new CognitoUser({
    Username: email,
    Pool: userPool,
  });

  return new Promise<ForgotPasswordResult>((resolve, reject) =>
    cognitoUser.forgotPassword({
      onSuccess: resolve,
      onFailure: reject,
    })
  );
}

export type ResetPasswordParams = {
  email: string;
  newPassword: string;
  verificationCode: string;
};

export async function resetPassword({
  email,
  newPassword,
  verificationCode,
}: ResetPasswordParams) {
  const cognitoUser = new CognitoUser({
    Username: email,
    Pool: userPool,
  });

  return new Promise((resolve, reject) =>
    cognitoUser.confirmPassword(verificationCode, newPassword, {
      onSuccess: resolve,
      onFailure: reject,
    })
  );
}

export async function logout() {
  const user = getCurrentUser();

  if (!user) throw 'UserNotFoundException';

  return new Promise<string>((resolve, reject) =>
    user.getSession((err: unknown, session: CognitoUserSession) => {
      if (err) {
        reject(err);
      } else {
        user.globalSignOut({
          onSuccess: resolve,
          onFailure: reject,
        });
      }
    })
  );
}

// note: this can also return things like 'NotAuthorizedException'
// see
// - https://github.com/aws-amplify/amplify-js/blob/df95ea3724eb6406f64b03f25086cd3e8644cb5f/packages/amazon-cognito-identity-js/src/CognitoUser.js#L1431
// - https://github.com/aws-amplify/amplify-js/blob/df95ea3724eb6406f64b03f25086cd3e8644cb5f/packages/amazon-cognito-identity-js/src/CognitoUser.js#L1449
export function getSession(
  cognitoUser: CognitoUser
): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    cognitoUser.getSession((err: unknown, session: CognitoUserSession) => {
      if (err) reject(err);
      else resolve(session);
    });
  });
}

export function refreshSession(
  cognitoUser: CognitoUser,
  refreshToken: CognitoRefreshToken
) {
  return new Promise((resolve, reject) => {
    cognitoUser.refreshSession(refreshToken, (err, result) => {
      if (err) reject(err);
      else resolve(result);
    });
  });
}

export async function changePassword(oldPassword: string, newPassword: string) {
  const user = getCurrentUser();

  if (!user) throw 'UserNotFoundException';

  const session = await getSession(user);

  // Try to refresh session when not valid anymore.
  if (!session.isValid()) {
    await refreshSession(user, session.getRefreshToken());
  }

  return new Promise<void>((resolve, reject) =>
    user.changePassword(oldPassword, newPassword, (err) => {
      if (err) reject(err);
      else resolve();
    })
  );
}

export function getCurrentUser() {
  return userPool.getCurrentUser();
}

export function updateAttributes(
  user: CognitoUser,
  attributes: (CognitoUserAttribute | ICognitoUserAttributeData)[]
) {
  return new Promise<string | undefined>((resolve, reject) =>
    user.updateAttributes(attributes, (err, result) => {
      if (err) reject(err);
      else resolve(result);
    })
  );
}

function setUserMfaPreference(
  user: CognitoUser,
  smsMfaSettings: IMfaSettings | null,
  softwareTokenMfaSettings: IMfaSettings | null
) {
  return new Promise<string | undefined>((resolve, reject) =>
    user.setUserMfaPreference(
      smsMfaSettings,
      softwareTokenMfaSettings,
      (err, result) => {
        if (err) reject();
        else resolve(result);
      }
    )
  );
}

export async function updatePhoneNumber(
  user: CognitoUser,
  phoneNumber: string
) {
  await getSession(user);

  await updateAttributes(user, [
    {
      Name: 'phone_number',
      // remove spaces to avoid this error:
      // {
      //   code: "InvalidParameterException",
      //   name: "InvalidParameterException",
      //   message: "Invalid phone number format."
      // }
      Value: phoneNumber.replace(/ /g, ''),
    },
  ]);

  await setUserMfaPreference(
    user,
    {
      PreferredMfa: false,
      Enabled: false,
    },
    {
      PreferredMfa: true,
      Enabled: true,
    }
  );
}

export async function resendVerificationCode(user: CognitoUser) {
  return new Promise((resolve, reject) =>
    user.getAttributeVerificationCode('phone_number', {
      onSuccess: resolve,
      onFailure: reject,
    })
  );
}

function verifyAttribute(
  user: CognitoUser,
  attributeName: string,
  confirmationCode: string
) {
  return new Promise<string>((resolve, reject) =>
    user.verifyAttribute(attributeName, confirmationCode, {
      onFailure: reject,
      onSuccess: resolve,
    })
  );
}

export async function verifyPhoneNumber(
  user: CognitoUser,
  confirmationCode: string
) {
  const session = await getSession(user);

  // see https://docs.aws.amazon.com/cognito-user-identity-pools/latest/APIReference/API_VerifyUserAttribute.html
  await verifyAttribute(user, 'phone_number', confirmationCode);

  // whenever we change the phone number, we need to verify it again.
  // if we do this cognito automatically sets a preference on the
  // authentication via software token.
  // this preference needs to be disabled manually.
  // if we don't do this we land in the 'totp-required' case during
  // login instead of 'select-mfa' and authentication via SMS will not be
  // possible.
  await setUserMfaPreference(user, null, {
    PreferredMfa: false,
    Enabled: false,
  });

  return session;
}

export async function switchAuthMethodToSoftware(user: CognitoUser) {
  await getSession(user);

  await updateAttributes(user, [
    {
      Name: 'custom:auth_method',
      Value: 'software',
    },
  ]);
}
